import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { api, type NewsResponse, type Today, type TodayEvent } from '../../lib/api'
import { ItemCard } from './ItemCard'
import { parJour } from './joursQuiViennent'
import { AddSheet } from './AddSheet'
import { TabBar } from '../../components/TabBar'
import { layout, sousLaBarre } from '../../design/tokens'
import { BoutonActualiser } from '../../components/BoutonActualiser'
import { BoutonTheme } from '../../components/BoutonTheme'

/**
 * Planche 03 du cahier des charges — « Aujourd'hui ».
 *
 * Écran agrégateur : « uniquement des blocs actionnables et datés. Une section vide disparaît,
 * elle ne s'affiche jamais en creux. » En v1.0 il ne reste que les nouveautés — l'agenda et les
 * courses appartiennent au Lot 3.
 *
 * C'est le seul écran qui mélange plusieurs espaces, et le cahier l'autorise explicitement :
 * chaque carte porte donc le nom de son espace quand il y en a plusieurs.
 */
export function TodayScreen({ activeSpaceId }: { activeSpaceId: string | null }) {
  const [feuille, setFeuille] = useState(false)

  const { data, isPending } = useQuery<Today>({ queryKey: ['aujourdhui'], queryFn: api.today })

  // L'écran s'appelle « Aujourd'hui » : il ne montre donc que le jour. Le filtre est fait ici
  // et non côté serveur, parce que « aujourd'hui » dépend du fuseau de l'appareil, que le
  // serveur ignore.
  const debutDuJour = new Date()
  debutDuJour.setHours(0, 0, 0, 0)

  const duJour = (data?.recent ?? []).filter(
    (item) => new Date(item.createdAt) >= debutDuJour,
  )

  // Comptés sur l'espace courant seulement : les tuiles mènent à cet espace, pas à l'agrégat.
  const evenementsDeLEspace = (data?.agenda ?? []).filter((e) => e.spaceId === activeSpaceId).length
  const coursesDeLEspace = (data?.shopping ?? [])
    .filter((liste) => liste.spaceId === activeSpaceId)
    .reduce((total, liste) => total + liste.remaining, 0)

  // Ce qui a été ajouté avant aujourd'hui. L'écran n'en montrait rien : un jour sans ajout le
  // laissait vide sur les deux tiers de sa hauteur, alors que c'est l'écran qu'on ouvre le plus.
  // Six suffisent — au-delà, c'est l'écran « Récents » qu'on veut.
  const plusAnciennes = (data?.recent ?? [])
    .filter((item) => !duJour.some((recente) => recente.id === item.id))
    .slice(0, 6)

  const plusieursEspaces =
    new Set([...duJour, ...plusAnciennes].map((item) => item.spaceId)).size > 1

  const quelqueChose =
    (data?.agenda.length ?? 0) > 0 ||
    (data?.upcoming.length ?? 0) > 0 ||
    (data?.shopping.length ?? 0) > 0 ||
    (data?.recent.length ?? 0) > 0

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      {/* « flex-wrap » et non une seule rangée : quatre boutons de 48 px et le titre en 34 px
          font 396 px, soit plus que les 350 px utiles d'une colonne mobile. Ils débordaient hors
          de l'écran, le dernier coupé (correction S55). Ils passent maintenant à la ligne quand
          la place manque, et restent sur une seule rangée dès qu'elle suffit. */}
      <header className="flex flex-wrap items-start justify-between gap-3" style={{ paddingBlock: 16 }}>
        <div className="min-w-0">
          <p className="text-label font-semibold uppercase text-ink-muted">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="mt-1 font-serif text-screen">Aujourd'hui</h1>
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <Cloche />
          {/* La loupe d'Aujourd'hui cherche dans tous les espaces : c'est l'écran d'accueil, et
              le seul qui les agrège déjà. Celle de Récents, elle, se borne au filtre en place. */}
          <Link
            to="/recherche"
            aria-label="Rechercher dans tous les espaces"
            title="Rechercher"
            className="flex shrink-0 items-center justify-center rounded-full border border-border bg-card text-accent"
            style={{ width: layout.minimumTouchTarget, height: layout.minimumTouchTarget }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </Link>
          <BoutonTheme />
          <BoutonActualiser />
        </span>
      </header>

      {/* Agenda et courses n'ont pas d'onglet : ces deux tuiles sont leur seul accès. Elles
          étaient auparavant deux liens de 13 px rendus après la barre d'onglets, où rien ne les
          distinguait d'une mention de bas de page. Elles restent visibles même quand la journée
          est vide — c'est justement là qu'on va consulter l'agenda. */}
      {activeSpaceId ? (
        <nav className="mb-6 grid grid-cols-2" style={{ gap: layout.gridGap }}>
          <Tuile
            to={`/espace/${activeSpaceId}/agenda`}
            libelle="Agenda"
            detail={
              evenementsDeLEspace > 0
                ? `${evenementsDeLEspace} aujourd'hui`
                : 'Rien de prévu'
            }
          />
          <Tuile
            to={`/espace/${activeSpaceId}/courses`}
            libelle="Courses"
            detail={
              coursesDeLEspace > 0 ? `${coursesDeLEspace} à prendre` : 'Liste à jour'
            }
          />
        </nav>
      ) : null}

      {isPending ? (
        <Squelette />
      ) : data && quelqueChose ? (
        <>
          {/* Ordre fixe de la planche 03 : agenda du jour d'abord. Section vide = section absente. */}
          {data.agenda.length > 0 ? (
            <section className="mb-6">
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">À l'agenda</h2>
              <ul className="flex flex-col gap-3">
                {data.agenda.map((evenement) => (
                  <CarteDAgenda key={evenement.id} evenement={evenement} />
                ))}
              </ul>
            </section>
          ) : null}

          {/* Les jours qui viennent. L'écran s'appelle « Aujourd'hui », mais un jour sans
              rendez-vous n'a rien à dire — et c'est le cas de la plupart. Savoir qu'un
              anniversaire tombe samedi est ce qu'on vient y chercher, et il fallait ouvrir
              l'agenda pour l'apprendre (décision L135). */}
          {data.upcoming.length > 0 ? (
            <section className="mb-6">
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                Les jours qui viennent
              </h2>
              {parJour(data.upcoming, (evenement) => new Date(evenement.startsAt), new Date()).map(
                (groupe) => (
                  <div key={groupe.libelle} className="mb-3">
                    <p className="mb-2 text-meta font-semibold">{groupe.libelle}</p>
                    <ul className="flex flex-col gap-2">
                      {groupe.elements.map((evenement) => (
                        <CarteDAgenda
                          key={`${evenement.id}-${evenement.startsAt}`}
                          evenement={evenement}
                        />
                      ))}
                    </ul>
                  </div>
                ),
              )}
            </section>
          ) : null}

          {/* La planche suggérait ce rappel au-delà de cinq fiches en attente. Il est
              descendu à une : le seuil n'avait de sens que tant qu'il n'y avait nulle part où
              aller. Il mène désormais à l'écran de tri, au lieu d'être une impasse. */}
          {data.unsortedCount > 0 && activeSpaceId ? (
            <Link
              to={`/espace/${activeSpaceId}/a-classer`}
              className="mb-4 flex items-center justify-between rounded-card border border-border bg-card p-4"
            >
              <span className="text-card-title font-semibold">
                {data.unsortedCount} idée{data.unsortedCount > 1 ? 's' : ''} à classer
              </span>
              <span className="text-meta font-semibold text-accent">Trier</span>
            </Link>
          ) : null}

          {/* La section « Courses en cours » de la planche 03 a été retirée : la tuile en
              haut de l'écran porte déjà le même compteur et mène au même endroit. Deux fois la
              même information à trois centimètres d'intervalle (correction S56). */}

          {duJour.length > 0 ? (
            <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
              Ajouté aujourd'hui
            </h2>
          ) : null}

          <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
            {duJour.map((item) => (
              // « flex flex-col » pour que le nom de l'espace reste collé à sa carte quelle
              // que soit la hauteur de la cellule. Les deux colonnes n'ont pas la même hauteur
              // — une carte sans image est compacte, c'est le choix de la variante 1i — et ce
              // n'est pas un défaut à corriger ici : ce serait toucher à un écran figé.
              <li key={item.id} className="flex flex-col">
                <ItemCard item={item} to={`/espace/${item.spaceId}/fiche/${item.id}`} />
                {plusieursEspaces ? (
                  <p className="mt-1 text-meta text-ink-muted">{item.spaceName}</p>
                ) : null}
              </li>
            ))}
          </ul>

          {plusAnciennes.length > 0 ? (
            <section style={{ marginTop: duJour.length > 0 ? 24 : 0 }}>
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                {duJour.length > 0 ? 'Avant cela' : 'Vos dernières idées'}
              </h2>

              <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
                {plusAnciennes.map((item) => (
                  <li key={item.id} className="flex flex-col">
                    <ItemCard item={item} to={`/espace/${item.spaceId}/fiche/${item.id}`} />
                    {plusieursEspaces ? (
                      <p className="mt-1 text-meta text-ink-muted">{item.spaceName}</p>
                    ) : null}
                  </li>
                ))}
              </ul>

              {activeSpaceId ? (
                <Link
                  to={`/espace/${activeSpaceId}/recents`}
                  className="mt-4 inline-block text-meta font-semibold text-accent"
                >
                  Voir toutes les idées
                </Link>
              ) : null}
            </section>
          ) : null}
        </>
      ) : (
        // « Aucun écran vide sans issue : tout état vide propose l'action suivante. »
        // « Un constat, puis une action » — section 03.6.
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Rien de neuf aujourd'hui.</p>
          <p className="mt-2 text-body text-ink-muted">
            {(data?.recent.length ?? 0) > 0
              ? 'Vos idées plus anciennes sont dans Récents.'
              : 'Collez un lien ou écrivez une note avec le bouton du bas.'}
          </p>
          {activeSpaceId && (data?.recent.length ?? 0) > 0 ? (
            <Link
              to={`/espace/${activeSpaceId}/recents`}
              className="mt-4 inline-block text-meta font-semibold text-accent"
            >
              Voir toutes les idées
            </Link>
          ) : null}
        </div>
      )}

      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={activeSpaceId} />

      {feuille && activeSpaceId ? (
        <AddSheet spaceId={activeSpaceId} onClose={() => setFeuille(false)} />
      ) : null}
    </main>
  )
}

/**
 * Cloche des nouveautés.
 *
 * Le compte est celui des fiches ajoutées par quelqu'un d'autre depuis la dernière consultation.
 * Sans pastille quand il n'y a rien : une cloche qui sonne dans le vide finit par ne plus être
 * regardée du tout.
 */
function Cloche() {
  const { data } = useQuery<NewsResponse>({
    queryKey: ['nouveautes'],
    queryFn: api.news,
  })

  const compte = data?.count ?? 0

  return (
    <Link
      to="/nouveautes"
      aria-label={compte > 0 ? `Nouveautés : ${compte}` : 'Nouveautés, rien de neuf'}
      title="Nouveautés"
      className="relative flex shrink-0 items-center justify-center rounded-full border border-border bg-card text-accent"
      style={{ width: layout.minimumTouchTarget, height: layout.minimumTouchTarget }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>

      {compte > 0 ? (
        <span
          className="absolute flex items-center justify-center rounded-full font-semibold"
          style={{
            top: -2,
            right: -2,
            minWidth: 18,
            height: 18,
            paddingInline: 4,
            fontSize: 11,
            background: 'var(--color-danger)',
            color: 'var(--color-card)',
          }}
        >
          {compte > 99 ? '99+' : compte}
        </span>
      ) : null}
    </Link>
  )
}

/**
 * Accès à un module. Même habillage que les cartes de contenu — bordure et aplat, jamais
 * d'ombre portée (section 03.4) — pour que ce soit lisiblement une surface tactile.
 */
function Tuile({ to, libelle, detail }: { to: string; libelle: string; detail: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col justify-center rounded-card border border-border bg-card px-4"
      style={{ minHeight: 72 }}
    >
      <span className="text-card-title font-semibold text-accent">{libelle}</span>
      <span className="mt-1 text-meta text-ink-muted">{detail}</span>
    </Link>
  )
}

/** Même géométrie que les cartes réelles : aucun saut de mise en page à l'arrivée des données. */
function Squelette() {
  return (
    <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
      {[0, 1, 2, 3].map((index) => (
        <li key={index} className="overflow-hidden rounded-card border border-border bg-card">
          <div className="w-full bg-skeleton-image" style={{ aspectRatio: '4 / 3' }} />
          <div className="p-3">
            <div className="rounded-field bg-sunken" style={{ height: 17 }} />
            <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '60%' }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * Un rendez-vous, tel qu'il se lit sur l'accueil.
 *
 * Extraite pour servir deux fois — l'agenda du jour et les jours qui viennent. Recopiée, elle
 * aurait divergé à la première retouche.
 */
function CarteDAgenda({ evenement }: { evenement: TodayEvent }) {
  return (
    <li className="rounded-card border border-border bg-card p-4">
      <div className="flex items-baseline gap-3">
        <span className="text-meta font-semibold text-accent" style={{ minWidth: 52 }}>
          {evenement.isAllDay
            ? 'Journée'
            : new Date(evenement.startsAt).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
        </span>
        <div>
          <p className="text-card-title font-semibold">{evenement.title}</p>
          {/* L'espace d'où vient le rendez-vous : cet écran est le seul à mêler plusieurs
              espaces, et sans lui on ne sait pas de quel foyer parle la ligne qu'on lit. */}
          <p className="mt-1 text-meta text-ink-muted">
            {evenement.spaceName}
            {evenement.note ? ` · ${evenement.note}` : ''}
          </p>
        </div>
      </div>
    </li>
  )
}
